import Button from "app/storybookComponents/Button";
import NoResultsIcon from "resources/images/no-results-transparent-back.png";
import SimpleCollapsibleCard from "app/storybookComponents/CollapsibleCards/SimpleCollapsibleCard";
import { useMemo, useState } from "react";
import {
  CelebrateAndSupports,
  CelebrateOrSupportType,
  GetCelebrateAndSupportUsersPayload,
} from "./types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CelebrateOrSupportCard from "./CelebrateOrSupportCard";
import CelebrateAndSupportFollowUpModal from "./Modals/CelebrateAndSupportFollowUpModal";
import { Dropdown } from "react-bootstrap";
import { getDropdownCheck } from "../AdminConsole/helpers";
import { useAppSelector } from "utils/redux/hooks";
import { selectTeamsByTeamId } from "../Global/slice";
import {
  selectDepartments,
  selectIsDepartmentsHidden,
} from "../AdminConsole/slice";
import { GetCelebrateAndSupportUsersPayloadTimeIntervals } from "./constants";
import SearchableDropdownAlt from "app/storybookComponents/SearchableInput/SearchableDropdownAlt";
import { getQueryParamsFromObject } from "../Global/helpers";
import Loading from "app/storybookComponents/Loading";
import { selectWeeklyCheckInCompanySettings } from "./slice";

interface Props {
  celebrateAndSupports: {
    [memoizedKey: string]: CelebrateAndSupports;
  };
  filteredTeamIds?: number[];
  filteredDepartmentIds?: number[];
  onGetWeeklyCheckInCelebrateAndSupport: (
    payload: GetCelebrateAndSupportUsersPayload
  ) => void;
  additionalQueryParams?: Record<string, string | boolean | number>;
  isCelebrateAndSupportsBeingFetched?: boolean;
  hideEntityPickers?: boolean;
  isSampleData?: boolean;
  sampleCelebrateAndSupports?: CelebrateAndSupports;
}

const CelebrateAndSupportContainer = ({
  celebrateAndSupports,
  filteredTeamIds,
  filteredDepartmentIds,
  onGetWeeklyCheckInCelebrateAndSupport,
  additionalQueryParams,
  isCelebrateAndSupportsBeingFetched,
  hideEntityPickers,
  isSampleData,
  sampleCelebrateAndSupports,
}: Readonly<Props>) => {
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const departmentsById = useAppSelector(selectDepartments);
  const areDepartmentsHidden = useAppSelector(selectIsDepartmentsHidden);
  const [isContainerCollapsed, setIsContainerCollapsed] = useState(true);
  const [navigationIndex, setNavigationIndex] = useState<{
    celebrate: number;
    support: number;
  }>({
    celebrate: 0,
    support: 0,
  });

  const [dropdownsState, setDropdownsState] =
    useState<GetCelebrateAndSupportUsersPayload>({
      timeFrame: "7Days",
    });

  const selectedCelebrateAndSupports = isSampleData
    ? sampleCelebrateAndSupports
    : celebrateAndSupports[
        getQueryParamsFromObject({
          ...dropdownsState,
          ...additionalQueryParams,
        })
      ];

  const [followUpModalState, setFollowUpModalState] = useState<{
    info: CelebrateOrSupportType;
    isSupport: boolean;
  } | null>(null);

  const getDeckNavigation = (totalLength: number, isSupport: boolean) => {
    const indexShowing = isSupport
      ? navigationIndex.support
      : navigationIndex.celebrate;
    const { timeUnit = "week" } =
      GetCelebrateAndSupportUsersPayloadTimeIntervals[
        dropdownsState.timeFrame
      ] ?? {};

    return (
      <div className="row-gap-12px align-items-center justify-content-between">
        <p className="grey-text">
          {indexShowing + 1} of {totalLength}{" "}
          {isSupport ? "support areas" : "wins"} this {timeUnit}
        </p>
        <div className="row-gap-8px">
          <Button
            variant="secondary-blue"
            onClick={() => {
              const key = isSupport ? "support" : "celebrate";
              setNavigationIndex((prevState) => ({
                ...prevState,
                [key]: indexShowing === 0 ? totalLength - 1 : indexShowing - 1,
              }));
            }}
          >
            <FontAwesomeIcon icon="arrow-left" />
          </Button>
          <Button
            variant="secondary-blue"
            onClick={() => {
              const key = isSupport ? "support" : "celebrate";
              const isLast = indexShowing + 1 >= totalLength;
              setNavigationIndex((prevState) => ({
                ...prevState,
                [key]: isLast ? 0 : indexShowing + 1,
              }));
            }}
          >
            <FontAwesomeIcon icon="arrow-right" />
          </Button>
        </div>
      </div>
    );
  };

  const getDeck = (arr: CelebrateOrSupportType[] = [], isSupport?: boolean) => {
    const total = arr?.length ?? 0;
    const label = isSupport ? "Support" : "Celebrate";
    const indexShowing = isSupport
      ? navigationIndex.support
      : navigationIndex.celebrate;
    const celebrateOrSupportObject = arr?.[indexShowing];
    return (
      <div className="column-gap-16px">
        <span className={`label-tag lg ${isSupport ? "gold" : "blue"} m-0`}>
          {label}
        </span>
        {total && celebrateOrSupportObject ? (
          <>
            <CelebrateOrSupportCard
              info={celebrateOrSupportObject}
              onShowFollowUpModal={() =>
                setFollowUpModalState({
                  info: celebrateOrSupportObject,
                  isSupport: !!isSupport,
                })
              }
              isSupportCard={!!isSupport}
              isSampleCard={isSampleData}
              hideScore={
                weeklyCheckInCompanySettings?.visibilityMode !== "open"
              }
            />
            {getDeckNavigation(total, !!isSupport)}
          </>
        ) : (
          <div
            className="empty-card"
            style={{ padding: "40px", height: "100%" }}
          >
            <img
              src={NoResultsIcon}
              alt="no results"
              style={{ marginBottom: "40px" }}
            />
            <span style={{ marginBottom: "8px" }}>
              No users to {label.toLowerCase()} currently
            </span>
            <p>
              Users{" "}
              {isSupport
                ? "who require support based on issues"
                : "to celebrate based on successes"}{" "}
              raised in their Manager Check-in responses will be shown here.
            </p>
          </div>
        )}
      </div>
    );
  };

  const getEntityTypeChangePayload = (eventKey: string) => {
    const updatedState: GetCelebrateAndSupportUsersPayload = {
      timeFrame: dropdownsState.timeFrame,
    };
    // if eventKey is team then get the first team that is not sample
    if (eventKey === "team") {
      const firstTeam = Object.values(teamsById).find(
        (team) => !team.isSampleTeam
      );
      if (firstTeam) {
        updatedState.teamId = firstTeam.teamId;
      }
    }

    // if eventKey is department then get the first department that is not sample
    if (eventKey === "department") {
      const firstDepartment = Object.values(departmentsById).find(
        (department) => department.companyAccountId !== 0
      );
      if (firstDepartment) {
        updatedState.departmentId = firstDepartment.departmentId;
      }
    }

    return updatedState;
  };

  const onEntityTypeChange = (eventKey: string | null) => {
    if (!eventKey) {
      return null;
    }

    const updatedState = getEntityTypeChangePayload(eventKey);
    setDropdownsState(updatedState);
    onGetWeeklyCheckInCelebrateAndSupport(updatedState);
  };

  const getDatePicker = () => {
    const timeFrame = dropdownsState.timeFrame;
    return (
      <Dropdown
        onSelect={(eventKey) => {
          setDropdownsState((prev) => {
            const updatedState = {
              ...prev,
              timeFrame:
                eventKey as GetCelebrateAndSupportUsersPayload["timeFrame"],
            };
            onGetWeeklyCheckInCelebrateAndSupport(updatedState);
            return updatedState;
          });
        }}
      >
        <Dropdown.Toggle
          variant="light"
          id="dropdown-basic"
          className="row-gap-16px align-items-center"
          disabled={isCelebrateAndSupportsBeingFetched}
        >
          {
            GetCelebrateAndSupportUsersPayloadTimeIntervals[timeFrame]
              .dropdownLabel
          }
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item eventKey="7Days">
            {getDropdownCheck(timeFrame === "7Days")}
            {
              GetCelebrateAndSupportUsersPayloadTimeIntervals["7Days"]
                .dropdownLabel
            }
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item eventKey="30Days">
            {getDropdownCheck(timeFrame === "30Days")}
            {
              GetCelebrateAndSupportUsersPayloadTimeIntervals["30Days"]
                .dropdownLabel
            }
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item eventKey="365Days">
            {getDropdownCheck(timeFrame === "365Days")}
            {
              GetCelebrateAndSupportUsersPayloadTimeIntervals["365Days"]
                .dropdownLabel
            }
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };
  const teamOptions = useMemo(() => {
    let teamObjs = Object.values(teamsById);
    if (filteredTeamIds) {
      teamObjs = teamObjs.filter((team) =>
        filteredTeamIds.includes(team.teamId)
      );
    }

    return teamObjs.map((team) => ({
      value: `${team.teamId}`,
      label: team.teamName ?? "",
    }));
  }, [teamsById, filteredTeamIds]);

  const departmentOptions = useMemo(() => {
    if (areDepartmentsHidden) {
      return [];
    }
    const departmentObjs = Object.values(departmentsById).filter(
      (department) => {
        if (department.companyAccountId === 0) {
          return false;
        }
        if (filteredDepartmentIds) {
          return filteredDepartmentIds.includes(department.departmentId);
        }
        return true;
      }
    );

    return departmentObjs.map((department) => ({
      value: `${department.departmentId}`,
      label: department.name ?? "",
    }));
  }, [departmentsById, filteredDepartmentIds, areDepartmentsHidden]);

  const getSelectedOption = () => {
    if (dropdownsState.teamId) {
      const selectedTeam = teamsById[dropdownsState.teamId];
      return selectedTeam?.teamName ?? "Team";
    } else if (dropdownsState.departmentId) {
      const selectedDepartment = departmentsById[dropdownsState.departmentId];
      return selectedDepartment?.name ?? "Department";
    }
    return null;
  };

  const getEntityPicker = () => {
    if (!dropdownsState.departmentId && !dropdownsState.teamId) {
      return null;
    }
    let options: { value: string; label: string }[] = [];
    if (dropdownsState.teamId) {
      options = teamOptions;
    } else if (dropdownsState.departmentId) {
      options = departmentOptions;
    } else {
      return null;
    }

    return (
      <SearchableDropdownAlt
        selectedValue={getSelectedOption()}
        options={options}
        disabled={isCelebrateAndSupportsBeingFetched}
        onSelect={(selectedValue) => {
          const selectedId = Number(selectedValue);
          setDropdownsState((prev) => {
            const updatedState = {
              ...prev,
              teamId: prev.teamId ? selectedId : undefined,
              departmentId: prev.departmentId ? selectedId : undefined,
            };
            onGetWeeklyCheckInCelebrateAndSupport(updatedState);
            return updatedState;
          });
        }}
      />
    );
  };
  const getEntityTypePicker = () => {
    // no teams and (no department or areDepartmentsHidden) then hide entity type picker
    if (
      teamOptions.length === 0 &&
      (!departmentOptions.length || areDepartmentsHidden)
    ) {
      return null;
    }

    let selectedVal = "Full organization";
    if (dropdownsState.teamId) {
      selectedVal = "By team";
    } else if (dropdownsState.departmentId) {
      selectedVal = "By department";
    }
    return (
      <Dropdown
        onSelect={(eventKey) => {
          onEntityTypeChange(eventKey);
        }}
      >
        <Dropdown.Toggle
          variant="light"
          id="dropdown-basic"
          className="row-gap-16px align-items-center"
          disabled={isCelebrateAndSupportsBeingFetched}
        >
          {selectedVal}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item eventKey="organization">
            {getDropdownCheck(
              !dropdownsState.teamId && !dropdownsState.departmentId
            )}
            Full organization
          </Dropdown.Item>

          {teamOptions?.length && !isSampleData ? (
            <>
              <Dropdown.Divider />
              <Dropdown.Item eventKey="team">
                {getDropdownCheck(!!dropdownsState.teamId)}
                By team
              </Dropdown.Item>
            </>
          ) : null}
          {!areDepartmentsHidden && !isSampleData && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item eventKey="department">
                {getDropdownCheck(!!dropdownsState.departmentId)}
                By department
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const getContainerBody = () => {
    if (isCelebrateAndSupportsBeingFetched) {
      return <Loading />;
    }

    return (
      <div className="celebrate-and-support-container__body">
        {getDeck(selectedCelebrateAndSupports?.celebrate)}
        {getDeck(selectedCelebrateAndSupports?.support, true)}
      </div>
    );
  };

  return (
    <>
      <CelebrateAndSupportFollowUpModal
        celebrationOrSupportItem={followUpModalState?.info ?? null}
        isSupport={followUpModalState?.isSupport}
        onClose={() => {
          setFollowUpModalState(null);
        }}
        isSample={isSampleData}
      />
      <SimpleCollapsibleCard
        title={<p className="grey-text fw-bold">Celebrate and Support</p>}
        isOpen={!isContainerCollapsed}
        setIsOpen={() => {
          setIsContainerCollapsed(!isContainerCollapsed);
        }}
        collapseText="Hide chart"
        expandText="Show chart"
      >
        <div className="celebrate-and-support-container">
          <div className="row-gap-12px align-items-center">
            {!hideEntityPickers && (
              <>
                {getEntityTypePicker()}
                {getEntityPicker()}
              </>
            )}
            {getDatePicker()}
          </div>
          {getContainerBody()}
        </div>
      </SimpleCollapsibleCard>
    </>
  );
};

export default CelebrateAndSupportContainer;
